import { FC } from 'react';
import { faMessageLines } from '@fortawesome/pro-regular-svg-icons';
import { Flex } from 'portal-commons';

import CampaignAsset from '../CampaignAsset';
import RcsSectionBlock from './RcsSectionBlock';
import InfoItemLayout from './InfoItemLayout';
import InfoItem from './InfoItem';
import StatusIndicator from './StatusIndicator';
import { RcsCampaign } from '../../types';
import { snakeToTitleCase } from '@/utils';
import { BrandAsset, BrandAssetType } from '@/apis/core/types';

interface CampaignDetailsSectionProps {
  campaign?: RcsCampaign;
  banner?: BrandAsset;
  logo?: BrandAsset;
  onEdit: () => void;
  editable?: boolean;
}

const CampaignDetailsSection: FC<CampaignDetailsSectionProps> = ({
  campaign,
  banner,
  logo,
  onEdit,
  editable,
}) => {
  return (
    <RcsSectionBlock
      icon={faMessageLines}
      title="Campaign Details"
      showDivider={true}
      editable={editable}
      onEditClick={onEdit}
    >
      <InfoItemLayout.Vertical>
        <InfoItemLayout.Horizontal>
          <InfoItem disableFallback>
            {logo && (
              <CampaignAsset
                type={BrandAssetType.Logo}
                name={logo.name}
                uuid={logo.attachmentUuid}
                status={logo.verificationStatus}
                dimension={{ width: '127px', height: '127px' }}
              />
            )}
          </InfoItem>
          <InfoItem disableFallback>
            {banner && (
              <CampaignAsset
                type={BrandAssetType.Banner}
                name={banner.name}
                uuid={banner.attachmentUuid}
                status={banner.verificationStatus}
                dimension={{ width: '379px', height: '127px' }}
              />
            )}
          </InfoItem>
        </InfoItemLayout.Horizontal>

        <InfoItemLayout.Horizontal>
          <InfoItemLayout.Vertical>
            <InfoItem title="ID">{campaign?.uid}</InfoItem>
            <InfoItem title="Display Name">{campaign?.displayName}</InfoItem>
            <InfoItem title="Background Color">
              <Flex sx={{ alignItems: 'center' }}>
                <StatusIndicator
                  shape="square"
                  color={`#${campaign?.color}`}
                  size="16px"
                />
                #{campaign?.color}
              </Flex>
            </InfoItem>
            <InfoItem title="Hosting Region">
              {snakeToTitleCase(campaign?.hostingRegion)}
            </InfoItem>
            <InfoItem title="Billing Category">
              {snakeToTitleCase(campaign?.billingCategory)}
            </InfoItem>
          </InfoItemLayout.Vertical>
          <InfoItemLayout.Vertical>
            <InfoItem title="Short Description" sx={{ flexGrow: 0 }}>
              {campaign?.shortDescription}
            </InfoItem>
            <InfoItem title="Use Case" sx={{ flexGrow: 0 }}>
              {campaign?.usecase}
            </InfoItem>
            <InfoItem title="Terms of Use URL" sx={{ flexGrow: 0 }}>
              {campaign?.termsAndConditionsLink}
            </InfoItem>
            <InfoItem title="Privacy Policy URL" sx={{ flexGrow: 0 }}>
              {campaign?.privacyPolicyLink}
            </InfoItem>
          </InfoItemLayout.Vertical>
        </InfoItemLayout.Horizontal>

        <InfoItemLayout.Horizontal sx={{ height: '18px' }} />
      </InfoItemLayout.Vertical>
      <InfoItemLayout.Vertical sx={{ gap: 'xs' }}>
        {campaign?.websites?.map((website, index) => (
          <InfoItemLayout.Horizontal key={`website-${index}`}>
            <InfoItem title={`${index === 0 ? 'Primary ' : ''}Website`}>
              {website}
            </InfoItem>
            <InfoItem
              title={`Label for ${index === 0 ? 'Primary ' : ''}Website`}
            >
              {campaign?.websiteLabels?.[index]}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        ))}
        {campaign?.phones?.map((phone, index) => (
          <InfoItemLayout.Horizontal
            key={`phone-${index}`}
            sx={{ mt: `${index === 0 ? '12px' : '0'}` }}
          >
            <InfoItem title={`${index === 0 ? 'Primary ' : ''}Phone Number`}>
              {phone}
            </InfoItem>
            <InfoItem
              title={`Label for ${index === 0 ? 'Primary ' : ''}Phone Number`}
            >
              {campaign?.phoneLabels?.[index]}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        ))}
        {campaign?.emails?.map((email, index) => (
          <InfoItemLayout.Horizontal
            key={`email-${index}`}
            sx={{ mt: `${index === 0 ? '12px' : '0'}` }}
          >
            <InfoItem title={`${index === 0 ? 'Primary ' : ''}Email ID`}>
              {email}
            </InfoItem>
            <InfoItem
              title={`Label for ${index === 0 ? 'Primary ' : ''}Email ID`}
            >
              {campaign?.emailLabels?.[index]}
            </InfoItem>
          </InfoItemLayout.Horizontal>
        ))}
      </InfoItemLayout.Vertical>
    </RcsSectionBlock>
  );
};

export default CampaignDetailsSection;
